import type { Component, Issue, Member, Sprint } from '@tasks/contracts';

import { Avatar } from '../../components/Avatar';
import { Chip } from '../../components/Chip';
import { Select, type SelectOption } from '../../components/Select';
import { Input } from '../../components/Input';
import { PRIORITY_COLOR_VAR } from '../../lib/types';
import type { Priority } from '../../lib/types';
import { formatPoints } from '../../lib/format';
import { PRIORITY_ORDER, priorityLabels, sprintStateLabels } from '../../i18n/enums';
import styles from './IssuePropertiesPanel.module.css';

const PRIORITY_OPTIONS: readonly SelectOption<Priority>[] = PRIORITY_ORDER.map((value) => ({
  value,
  label: priorityLabels[value],
  tone: PRIORITY_COLOR_VAR[value],
}));

export type IssuePropertiesPanelProps = {
  readonly issue: Issue;
  readonly members: readonly Member[];
  readonly sprints: readonly Sprint[];
  /** Справочник компонентов проекта: архивные в выбор не попадают. */
  readonly components: readonly Component[];
  readonly canEdit: boolean;
  readonly onPatch: (patch: { readonly [key: string]: unknown }) => void;
  readonly onAddLabel: (label: string) => void;
  readonly onRemoveLabel: (label: string) => void;
};

/**
 * Правый столбец свойств (docs/06 §3.2). Сохранение — при blur/выборе,
 * без кнопки «Save»; ошибку обрабатывает вызывающий код (откат + тост).
 */
export function IssuePropertiesPanel({
  issue,
  members,
  sprints,
  components,
  canEdit,
  onPatch,
  onAddLabel,
  onRemoveLabel,
}: IssuePropertiesPanelProps) {
  const memberOptions: readonly SelectOption<string>[] = members.map((member) => ({
    value: member.user.id,
    label: member.user.displayName,
  }));
  const sprintOptions: readonly SelectOption<string>[] = sprints.map((sprint) => ({
    value: sprint.id,
    label: `${sprint.name} · ${sprintStateLabels[sprint.state]}`,
  }));

  return (
    <aside className={styles['panel']} aria-label="Свойства задачи">
      <Field label="Статус">
        <span className={styles['static']}>{issue.status.name}</span>
      </Field>

      <Field label="Исполнитель">
        <div className={styles['inline']}>
          <Avatar user={issue.assignee} size="xs" />
          <Select
            options={memberOptions}
            value={issue.assignee?.id ?? null}
            placeholder="Не назначен"
            disabled={!canEdit}
            clearable
            onClear={() => onPatch({ assigneeId: null })}
            onChange={(value) => onPatch({ assigneeId: value })}
          />
        </div>
      </Field>

      <Field label="Приоритет">
        <Select
          options={PRIORITY_OPTIONS}
          value={issue.priority}
          disabled={!canEdit}
          onChange={(value) => onPatch({ priority: value })}
        />
      </Field>

      <Field label="Спринт">
        <Select
          options={sprintOptions}
          value={issue.sprint?.id ?? null}
          placeholder="Без спринта"
          disabled={!canEdit}
          clearable
          onClear={() => onPatch({ sprintId: null })}
          onChange={(value) => onPatch({ sprintId: value })}
        />
      </Field>

      <Field label="Story points">
        <Input
          inputSize="sm"
          inputMode="decimal"
          defaultValue={issue.storyPoints === null ? '' : String(issue.storyPoints)}
          disabled={!canEdit}
          onBlur={(event) => {
            const raw = event.target.value.trim();
            const parsed = raw.length === 0 ? null : Number(raw);
            if (parsed !== null && (Number.isNaN(parsed) || parsed < 0 || parsed > 100)) {
              event.target.value = issue.storyPoints === null ? '' : String(issue.storyPoints);
              return;
            }
            if (parsed !== issue.storyPoints) {
              onPatch({ storyPoints: parsed });
            }
          }}
        />
      </Field>

      <Field label="Срок">
        <Input
          inputSize="sm"
          type="date"
          defaultValue={issue.dueDate ?? ''}
          disabled={!canEdit}
          onBlur={(event) => {
            const value = event.target.value.length === 0 ? null : event.target.value;
            if (value !== issue.dueDate) {
              onPatch({ dueDate: value });
            }
          }}
        />
      </Field>

      <Field label="Эпик">
        <span className={styles['static']}>{issue.epic ? `${issue.epic.key} · ${issue.epic.summary}` : '—'}</span>
      </Field>

      <Field label="Метки">
        <LabelEditor labels={issue.labels} canEdit={canEdit} onAdd={onAddLabel} onRemove={onRemoveLabel} />
      </Field>

      <Field label="Компоненты">
        <ComponentEditor
          issue={issue}
          components={components}
          canEdit={canEdit}
          onPatch={onPatch}
        />
      </Field>

      <Field label="Автор">
        <span className={styles['static']}>{issue.reporter.displayName}</span>
      </Field>

      <Field label="Создана">
        <span className={styles['static']}>{issue.createdAt.slice(0, 10)}</span>
      </Field>

      <Field label="Оценка / потрачено">
        <span className={styles['static']}>
          {issue.originalEstimate === null ? '—' : `${issue.originalEstimate}м`} / {issue.timeSpent}м
        </span>
      </Field>

      {issue.subtasks.length > 0 ? (
        <Field label={`Подзадачи (${issue.subtasks.filter((task) => task.statusId === issue.status.id).length}/${issue.subtasks.length})`}>
          <span className={styles['static']}>{issue.subtasks.map((task) => task.key).join(', ')}</span>
        </Field>
      ) : null}

      {issue.storyPoints === null ? null : (
        <p className={styles['pointsNote']}>Оценка: {formatPoints(issue.storyPoints)} sp</p>
      )}
    </aside>
  );
}

function Field({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <div className={styles['field']}>
      <span className={styles['label']}>{label}</span>
      <div className={styles['control']}>{children}</div>
    </div>
  );
}

function LabelEditor({
  labels,
  canEdit,
  onAdd,
  onRemove,
}: {
  readonly labels: readonly string[];
  readonly canEdit: boolean;
  readonly onAdd: (label: string) => void;
  readonly onRemove: (label: string) => void;
}) {
  return (
    <div className={styles['labels']}>
      {labels.map((label) => (
        <Chip key={label} removable={canEdit} onRemove={() => onRemove(label)}>
          {label}
        </Chip>
      ))}
      {canEdit ? (
        <input
          className={styles['labelInput']}
          placeholder="+ метка"
          aria-label="Добавить метку"
          onKeyDown={(event) => {
            if (event.key !== 'Enter') {
              return;
            }
            const value = event.currentTarget.value.trim().replace(/\s+/g, '-').toLowerCase();
            if (value.length > 0 && !labels.includes(value)) {
              onAdd(value);
            }
            event.currentTarget.value = '';
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Компоненты задачи (docs/13 §7). Выбор — из справочника проекта, а не
 * свободным вводом: опечатка не создаёт новый компонент. Архивные компоненты
 * не предлагаются, но уже проставленные остаются видимыми и снимаемыми.
 */
function ComponentEditor({
  issue,
  components,
  canEdit,
  onPatch,
}: {
  readonly issue: Issue;
  readonly components: readonly Component[];
  readonly canEdit: boolean;
  readonly onPatch: (patch: { readonly [key: string]: unknown }) => void;
}) {
  const currentIds = issue.components.map((component) => component.id);
  const options: readonly SelectOption<string>[] = components
    .filter((component) => !currentIds.includes(component.id))
    .map((component) => ({ value: component.id, label: component.name }));

  const apply = (ids: readonly string[]): void => {
    onPatch({ components: [...ids] });
  };

  return (
    <div className={styles['labels']}>
      {issue.components.map((component) => (
        <Chip
          key={component.id}
          removable={canEdit}
          onRemove={() => apply(currentIds.filter((id) => id !== component.id))}
        >
          {component.name}
        </Chip>
      ))}
      {canEdit && options.length > 0 ? (
        <div className={styles['componentSelect']}>
          <Select
            options={options}
            value={null}
            placeholder="+ компонент"
            onChange={(value) => apply([...currentIds, value])}
          />
        </div>
      ) : null}
      {!canEdit && issue.components.length === 0 ? (
        <span className={styles['static']}>—</span>
      ) : null}
    </div>
  );
}
